import React, { useState } from 'react';
import useExitIntent from '../hooks/useExitIntent';
import ExitPopup from './ExitPopup';

interface ExitPopupDemoProps {
  prospectId: string;
  // Options pour personnaliser le comportement de l'exit popup
  options?: {
    sensitivity?: number;
    delay?: number;
    onlyOnce?: boolean;
  };
}

/**
 * Composant de démonstration pour l'exit pop-up
 * Montre comment intégrer l'exit pop-up dans une page existante
 */
const ExitPopupDemo: React.FC<ExitPopupDemoProps> = ({ 
  prospectId,
  options = {}
}) => {
  // État pour suivre si l'utilisateur a déjà soumis une redirection
  const [hasSubmitted, setHasSubmitted] = useState(false);
  
  // Utilisation du hook useExitIntent pour détecter quand l'utilisateur tente de quitter la page
  const { showExitIntent, resetExitIntent } = useExitIntent({
    sensitivity: options.sensitivity || 20,
    delay: options.delay || 1000,
    onlyOnce: options.onlyOnce !== undefined ? options.onlyOnce : true,
    // Désactiver la détection si l'utilisateur a déjà soumis une redirection
    disabled: hasSubmitted
  });
  
  // Gestion de la fermeture du popup
  const handleClose = () => {
    resetExitIntent();
  };
  
  // Gestion de la soumission du formulaire
  const handleSubmit = () => {
    setHasSubmitted(true);
  };
  
  return (
    <>
      {/* Afficher l'exit pop-up uniquement lorsque l'utilisateur tente de quitter la page */}
      {showExitIntent && (
        <ExitPopup
          prospectId={prospectId}
          onClose={handleClose}
          onSubmit={handleSubmit}
        />
      )}
      
      {/* Bouton de test pour afficher l'exit pop-up (uniquement en développement) */}
      {process.env.NODE_ENV === 'development' && (
        <div className="fixed bottom-4 right-4">
          <button
            onClick={() => {
              // Réinitialiser l'état pour permettre de tester à nouveau
              setHasSubmitted(false);
              // Afficher l'exit pop-up
              resetExitIntent();
              setTimeout(() => {
                // Simuler un exit intent
                document.dispatchEvent(new MouseEvent('mouseleave', {
                  clientY: 0,
                  bubbles: true
                }));
              }, 100);
            }}
            className="px-4 py-2 bg-gray-800 text-white rounded-md hover:bg-gray-700"
          >
            Tester l'exit pop-up
          </button>
        </div>
      )}
    </>
  );
};

export default ExitPopupDemo;
